import {
    collectProvenanceDetailBinding,
    validateProvenanceDetailBindings,
} from "./provenance-detail-bindings.js";
import { CALCULATOR_FACT_KEY_FIELDS } from "./calculator-fact-key-contract.js";
import { provenanceIndicatorColorForTone } from "./provenance-tone.js";

export const PROVENANCE_CALLOUT_ID = "fishy-provenance-tooltip";

function trimString(value) {
    const normalized = String(value ?? "").trim();
    return normalized || "";
}

function escapeHtml(value) {
    return String(value ?? "").replace(
        /[&<>"']/g,
        (char) => ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            "\"": "&quot;",
            "'": "&#39;",
        })[char] || char,
    );
}

function formatText(text, vars = {}) {
    return String(text ?? "").replace(/\{\s*\$([A-Za-z0-9_]+)\s*\}/g, (_match, name) => {
        return Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : "";
    });
}

function languageHelper() {
    const helper = globalThis.window?.__fishystuffLanguage ?? globalThis.__fishystuffLanguage;
    return helper && typeof helper.t === "function" ? helper : null;
}

function provenanceText(key, fallback, vars = {}) {
    const normalizedKey = trimString(key);
    const helper = languageHelper();
    if (helper && normalizedKey) {
        const translated = helper.t(normalizedKey, vars);
        if (translated !== normalizedKey) {
            return String(translated);
        }
    }
    return formatText(fallback, vars);
}

const TYPED_PRESENCE_FACT_KINDS = new Set([
    "groupMembership",
    "groupPresence",
    "itemPresence",
]);
const TYPED_RATE_FACT_KINDS = new Set([
    "groupRate",
    "itemRate",
    "masteryPrizeRate",
]);
const TYPED_QUANTITY_FACT_KINDS = new Set([
    "itemQuantity",
    "itemQuantityDefault",
]);
const RATE_CONFIDENCE_VALUES = new Set(["known", "guessed"]);
const LAYER_SOURCE_TONES = Object.freeze({
    "calculator-base-v1": "database",
    "calculator-source-text-v1": "derived",
    "calculator-ranking-import-v1": "imported",
    "calculator-community-baseline-v1": "database",
    "calculator-manual-enrichment-v1": "manual",
    "calculator-user-v4": "overlay",
});

const TYPED_EVIDENCE_SOURCE_KINDS = Object.freeze({
    structuredSourceFact: "database",
    sourceTextInference: "derived",
    importedEnrichment: "imported",
    communityClaim: "community",
    manualEnrichment: "manual",
    user: "overlay",
    mixed: "mixed",
    unknown: "unknown",
});

function databaseBackedSource(layerId, evidenceClass) {
    const normalizedLayerId = trimString(layerId);
    if (Object.prototype.hasOwnProperty.call(LAYER_SOURCE_TONES, normalizedLayerId)) {
        return normalizedLayerId === "calculator-base-v1"
            || normalizedLayerId === "calculator-community-baseline-v1";
    }
    return evidenceClass === "structuredSourceFact";
}

function requireTypedFactKind(value) {
    if (
        typeof value !== "string"
        || !Object.prototype.hasOwnProperty.call(CALCULATOR_FACT_KEY_FIELDS, value)
    ) {
        throw new TypeError(
            `unsupported calculator provenance fact kind: ${String(value || "<empty>")}`,
        );
    }
    return value;
}

function requireTypedEvidenceSourceKind(value) {
    if (
        typeof value !== "string"
        || !Object.prototype.hasOwnProperty.call(TYPED_EVIDENCE_SOURCE_KINDS, value)
    ) {
        throw new TypeError(
            `unsupported calculator provenance evidence class: ${String(value || "<empty>")}`,
        );
    }
    return TYPED_EVIDENCE_SOURCE_KINDS[value];
}

/**
 * Resolves the established map/calculator provenance palette from exact typed
 * trace fields without inspecting source labels or free-form detail text.
 */
export function typedProvenanceIndicatorStyle(
    factKind,
    evidenceClass,
    {
        active = true,
        confidence = null,
        layerId = null,
        observationScope = null,
    } = {},
) {
    const normalizedFactKind = requireTypedFactKind(factKind);
    const sourceKind = requireTypedEvidenceSourceKind(evidenceClass);
    if (confidence !== null && !RATE_CONFIDENCE_VALUES.has(confidence)) {
        throw new TypeError(
            `unsupported calculator provenance rate confidence: ${String(confidence)}`,
        );
    }
    const quantityFact = TYPED_QUANTITY_FACT_KINDS.has(normalizedFactKind);
    const channel = TYPED_PRESENCE_FACT_KINDS.has(normalizedFactKind)
        ? "presence"
        : (TYPED_RATE_FACT_KINDS.has(normalizedFactKind) || quantityFact ? "rate" : "other");
    let sourceTone = sourceKind === "database"
        ? "database"
        : (sourceKind === "overlay" ? "overlay" : sourceKind);
    let sourceIcon = "";

    if (channel === "presence") {
        if (observationScope === "rankingFullyContained") {
            sourceTone = "ranking-full";
            sourceIcon = "ring-full";
        } else if (observationScope === "rankingPartialIntersection") {
            sourceTone = "ranking-partial";
            sourceIcon = "ring-partial";
        } else if (sourceKind === "community") {
            sourceTone = "community-presence";
        }
    } else if (TYPED_RATE_FACT_KINDS.has(normalizedFactKind)) {
        sourceTone = confidence === "known"
            ? "rate-known"
            : (
                confidence === null
                && databaseBackedSource(layerId, evidenceClass)
                ? "database"
                : "rate-uncertain"
            );
    } else if (channel === "rate") {
        if (sourceKind === "community") {
            sourceTone = "community";
        }
    }

    const rankingPresenceScope = channel === "presence" && (
        observationScope === "rankingFullyContained"
        || observationScope === "rankingPartialIntersection"
    );
    if (!rankingPresenceScope && !TYPED_RATE_FACT_KINDS.has(normalizedFactKind)) {
        sourceTone = LAYER_SOURCE_TONES[trimString(layerId)] ?? sourceTone;
    }

    const color = provenanceIndicatorColorForTone(sourceTone, { active });
    return Object.freeze({ color, sourceIcon, sourceTone });
}

export function provenanceAriaLabel(segment) {
    const label = trimString(segment?.label) || provenanceText("common.provenance.label.default", "Provenance");
    const source = trimString(segment?.sourceLabel)
        || provenanceText("common.provenance.source.unspecified", "Unspecified");
    const detail = trimString(segment?.detail)
        || provenanceClaimsText(segment?.claims);
    return detail ? `${label}: ${source}. ${detail}` : `${label}: ${source}.`;
}

function normalizeDetailCardRow(row, path) {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
        throw new TypeError(`${path} must be an object`);
    }
    const label = trimString(row.label);
    const value = trimString(row.value);
    if (!label || !value) {
        throw new TypeError(`${path} requires non-empty label and value`);
    }
    return Object.freeze({
        label,
        value,
        icon: trimString(row.icon),
        tone: trimString(row.tone),
    });
}

export function provenanceSummaryColor(value) {
    if (typeof value !== "string" || !/^#[0-9a-f]{8}$/i.test(value)) {
        throw new TypeError("card.summaryColor must be an eight-digit CSS hex color");
    }
    return value;
}

function normalizeDetailCard(card, path) {
    if (!card || typeof card !== "object" || Array.isArray(card)) {
        throw new TypeError(`${path} must be an object`);
    }
    const summary = trimString(card.summary);
    const badge = trimString(card.badge);
    if (!summary || !badge) {
        throw new TypeError(`${path} requires non-empty summary and badge`);
    }
    if (!Array.isArray(card.rows) || card.rows.length === 0) {
        throw new TypeError(`${path}.rows must contain the leading typed value row`);
    }
    return Object.freeze({
        summary,
        ...(card.summaryColor == null ? {} : { summaryColor: provenanceSummaryColor(card.summaryColor) }),
        badge,
        icon: trimString(card.icon),
        tone: trimString(card.tone),
        rows: Object.freeze(card.rows.map((row, index) => (
            normalizeDetailCardRow(row, `${path}.rows[${index}]`)
        ))),
    });
}

const CLAIM_STATUSES = new Set([
    "effective",
    "overridden",
    "refuted",
    "inactiveCondition",
]);

function normalizeClaim(claim, path) {
    if (!claim || typeof claim !== "object" || Array.isArray(claim)) {
        throw new TypeError(`${path} must be an object`);
    }
    const status = trimString(claim.status);
    if (!CLAIM_STATUSES.has(status)) {
        throw new TypeError(`${path}.status must be a supported provenance status`);
    }
    const laneId = trimString(claim.laneId);
    if (!laneId) {
        throw new TypeError(`${path}.laneId must be a non-empty stable fact identity`);
    }
    return Object.freeze({
        card: normalizeDetailCard(claim.card, `${path}.card`),
        laneId,
        status,
    });
}

export function normalizeProvenanceClaims(claims) {
    if (claims === undefined || claims === null) {
        return Object.freeze([]);
    }
    if (!Array.isArray(claims)) {
        throw new TypeError("provenance claims must be an array");
    }
    return Object.freeze(claims.map((claim, index) => (
        normalizeClaim(claim, `provenance claims[${index}]`)
    )));
}

export function provenanceClaimsText(claims) {
    return normalizeProvenanceClaims(claims)
        .map(({ card }) => [
            card.summary,
            card.badge,
            ...card.rows.map((row) => `${row.label}: ${row.value}`),
        ].join(" · "))
        .join(" | ");
}

export function serializeProvenanceClaims(claims) {
    const normalized = normalizeProvenanceClaims(claims);
    return normalized.length ? JSON.stringify(normalized) : null;
}

function conciseProvenanceAriaLabel(segment) {
    const label = trimString(segment?.label)
        || provenanceText("common.provenance.label.default", "Provenance");
    const source = trimString(segment?.sourceLabel)
        || provenanceText("common.provenance.source.unspecified", "Unspecified");
    return `${label}: ${source}.`;
}

export function provenanceRailMarkup(segments, { label, detailBindings = null } = {}) {
    if (!Array.isArray(segments)) {
        throw new TypeError("provenanceRailMarkup requires an array of segments");
    }
    if (detailBindings !== null) {
        validateProvenanceDetailBindings(detailBindings);
    }
    const railLabel = trimString(label)
        || provenanceText("common.provenance.label.default", "Provenance");
    const segmentMarkup = segments.map((segment) => {
        const segmentId = trimString(segment?.segmentId);
        const normalizedClaims = detailBindings === null
            ? null
            : normalizeProvenanceClaims(segment?.claims);
        const detail = trimString(segment?.detail)
            || provenanceClaimsText(
                normalizedClaims ?? segment?.claims,
            );
        const bindingKey = detailBindings === null
            ? null
            : collectProvenanceDetailBinding(
                detailBindings,
                normalizedClaims,
            );
        const serializedClaims = detailBindings === null
            ? serializeProvenanceClaims(segment?.claims)
            : null;
        const channel = trimString(segment?.channel);
        return `
            <button
                type="button"
                class="fishy-provenance-rail__segment${segment?.active === true ? "" : " is-inactive"}"
                style="--fishy-provenance-color:${escapeHtml(segment?.color)};"
                aria-label="${escapeHtml(
                    detailBindings === null
                        ? provenanceAriaLabel(segment)
                        : conciseProvenanceAriaLabel(segment),
                )}"
                aria-controls="${PROVENANCE_CALLOUT_ID}"
                aria-expanded="false"
                aria-haspopup="dialog"
                ${channel
                    ? `data-fishy-provenance-channel="${escapeHtml(channel)}"`
                    : ""}
                ${bindingKey === null
                    ? ""
                    : `data-fishy-provenance-binding="${bindingKey}"`}
                ${
                    serializedClaims
                        ? ` data-fishy-provenance-claims="${escapeHtml(serializedClaims)}"`
                        : ""
                }${
                    segmentId
                        ? ` data-calculator-provenance-segment-id="${escapeHtml(segmentId)}"`
                        : ""
                }
            ></button>
        `;
    }).join("");
    return `
        <span class="fishy-provenance-rail" aria-label="${escapeHtml(railLabel)}" role="group">
            ${segmentMarkup}
        </span>
    `;
}

export function provenanceIndicatorSampleMarkup({
    label = "Presence provenance",
    tone = "database",
} = {}) {
    const normalizedLabel = trimString(label) || "Presence provenance";
    const normalizedTone = trimString(tone) || "unknown";
    const color = provenanceIndicatorColorForTone(normalizedTone, {
        active: true,
    });
    return `
        <span
            class="fishy-provenance-rail"
            data-fishy-provenance-sample="${escapeHtml(normalizedTone)}"
            aria-label="${escapeHtml(normalizedLabel)}"
            role="img"
        >
            <span
                class="fishy-provenance-rail__segment"
                style="--fishy-provenance-color:${escapeHtml(color)};"
                aria-hidden="true"
            ></span>
        </span>
    `;
}
